import {
    AdditionalBusinessInfoForm, BankInformationForm, BusinessOwnersForm,
    DBAForm,
    LegalForm, NuveiApplicationForm, SalesProfileForm,
    DocumentType, SingleOwner
} from "../../models/nuvei-application.model";
import { AddressComponents, addressComponentsEmpty } from "../../../../../common/src/lib/models/address-comments.model";
import { isValidEmail } from "../../../../../common/src/lib/services";
import { isMoment } from "moment/moment";

export type FormErrors<O> = {
    [F in keyof O]?: string
}

export type ExistingDocuments = { [T in DocumentType ]?: boolean };
export type NuveiFormErrors<K extends keyof NuveiApplicationForm> = FormErrors<NuveiApplicationForm[K]>;
export type BusinessOwnersFormErrors = {
    owners: FormErrors<SingleOwner>[];
    guarantor?: string
};

export function validateLegalForm(form: LegalForm) {
    const errors: NuveiFormErrors<'legalForm'> = {};
    if(Object.keys(form).length === 0) {
        return ['businessName', 'ownershipType', 'mailingAddress', 'businessPhoneNumber', 'supportPhoneNumber', 'contactPhoneNumber', 'mobilePhoneNumber', 'businessEmail', 'contactEmail', 'businessCreationYear', 'businessCreationMonth', 'federalTaxID', 'address']
            .reduce((acc, curr) => {
                acc[curr as keyof typeof acc] = 'required';
                return acc;
            }, errors );
    }
    validateRequiredTextFields(form, errors, {
        'businessName': null,
        'ownershipType': null,
        'mailingAddress': 'mailingAddressSameAsAddress',
        'businessPhoneNumber': null,
        'supportPhoneNumber': 'supportPhoneNumberSameAsBusinessPhoneNumber',
        'contactPhoneNumber': 'contactPhoneNumberSameAsBusinessPhoneNumber',
        'mobilePhoneNumber': 'mobilePhoneNumberSameAsBusinessPhoneNumber',
        'businessEmail': null,
        'contactEmail': 'contactEmailSameAsBusinessEmail',
        'federalTaxID': null,
    });
    validateAddress('address', form.address, errors);
    validatePhoneNumberFields(form, errors, {
        'businessPhoneNumber': null,
        'supportPhoneNumber': 'supportPhoneNumberSameAsBusinessPhoneNumber',
        'contactPhoneNumber': 'contactPhoneNumberSameAsBusinessPhoneNumber',
        'mobilePhoneNumber': 'mobilePhoneNumberSameAsBusinessPhoneNumber',
    });
    validateEmailFields(form, errors, {
        'businessEmail': null,
        'contactEmail': 'contactEmailSameAsBusinessEmail',
    });
    validateDateFields(form, errors, {
        'creationDate': null
    });
    validateDashedNumberFields(form, errors, {
        'federalTaxID': 9
    });

    return errors;
}

export function validateDBAForm(form: DBAForm) {
    const errors: NuveiFormErrors<'dbaForm'> = {};
    if(Object.keys(form).length === 0) {
        return ['businessName', 'address', 'phoneNumber', 'creationDate', 'timezone', 'address']
            .reduce((acc, curr) => {
                acc[curr as keyof typeof acc] = 'required';
                return acc;
            }, errors );
    }
    validateRequiredTextFields(form, errors, {
        'businessName': null,
        'address': null,
        'phoneNumber': null,
        'timezone': null,
    });
    validatePhoneNumberFields(form, errors, {
        'phoneNumber': null,
    });
    validateAddress('address', form.address, errors);
    validateDateFields(form, errors, {
        'creationDate': null
    });

    return errors;
}

export function validateAdditionalBusinessInfoForm(form: AdditionalBusinessInfoForm) {
    const errors: NuveiFormErrors<'additionalBusinessInfoForm'> = {};
    if(Object.keys(form).length === 0) {
        return ['businessType', 'description', 'zoning', 'locale', 'squareFootage', 'refundPolicy']
            .reduce((acc, curr) => {
                acc[curr as keyof typeof acc] = 'required';
                return acc;
            }, errors );
    }
    validateRequiredTextFields(form, errors, {
        'businessType': null,
        'description': null,
        'zoning': null,
        'locale': null,
        'squareFootage': null,
    });
    if(form.hasRefundPolicy) {
        validateRequiredTextFields(form, errors, {
            'refundPolicy': null,
        });
    }
    return errors;
}

export function validateSalesProfileForm(form: SalesProfileForm) {
    const errors: NuveiFormErrors<'salesProfileForm'> = {};
    if(Object.keys(form).length === 0) {
        return ['avgVolumePerMonth', 'avgTicketSize', 'highTicket', 'expectedAMEXPercentage']
            .reduce((acc, curr) => {
                acc[curr as keyof typeof acc] = 'required';
                return acc;
            }, errors );
    }
    validateRequiredNumberFields(form, errors, {
        'avgVolumePerMonth': null,
        'avgTicketSize': null,
        'highTicket': null,
        'expectedAMEXPercentage': null,
    });
    return errors;
}

export function validateBusinessOwnersForm(form: BusinessOwnersForm) {
    if(Object.keys(form).length === 0) {
        return {
            owners: [
                ['firstName', 'lastName', 'phoneNumber', 'email', 'title', 'citizenship', 'ssn', 'address', 'ownershipPercentage', 'birthDate']
                    .reduce((acc, curr) => {
                        acc![curr as keyof typeof acc] = 'required';
                        console.log(acc)
                        return acc;
                    }, {} as FormErrors<SingleOwner> )
            ],
            guarantor: 'required'
        };
    }
    const errors: BusinessOwnersFormErrors = {
        owners: []
    };
    let totalPercentage = form.owners!.reduce((acc, curr) => {
        if(curr.ownershipPercentage != null) {
            acc += curr.ownershipPercentage;
        }
        return acc;
    }, 0);

    for(let i = 0; i < form.owners!.length; i++) {
        const ownerErrors: FormErrors<SingleOwner> = {}
        const ownerForm = form.owners![i];
        validateRequiredTextFields(ownerForm, ownerErrors, {
            'firstName': null,
            'lastName': null,
            'phoneNumber': null,
            'email': null,
            'title': null,
            'citizenship': null,
            'ssn': null,
        });
        validateAddress('address', ownerForm.address, ownerErrors);
        validatePhoneNumberFields(ownerForm, ownerErrors, {
            'phoneNumber': null,
        });
        validateEmailFields(ownerForm, ownerErrors, {
            'email': null,
        });
        validateDateFields(ownerForm, ownerErrors, {
            'birthDate': null
        });
        validateDashedNumberFields(ownerForm, ownerErrors, {
            'ssn': 9
        });
        if(!ownerForm.ownershipPercentage) {
            ownerErrors['ownershipPercentage'] = 'required';
        } else if(totalPercentage > 100) {
            ownerErrors['ownershipPercentage'] = 'total'
        }
        if(Object.keys(ownerErrors).length > 0)
            errors.owners[i] = ownerErrors;
    }
    if(form.guarantor! < 0 || form.guarantor! > (form.owners!.length - 1)) {
        errors.guarantor = 'invalid';
    }
    return errors;
}

export function validateBankInformationForm(form: BankInformationForm) {
    const errors: NuveiFormErrors<'bankInformationForm'> = {};
    if(Object.keys(form).length === 0) {
        return ['bankTransit', 'bankAccountNumber', 'associatedAddress']
            .reduce((acc, curr) => {
                acc[curr as keyof typeof acc] = 'required';
                return acc;
            }, errors );
    }
    validateRequiredTextFields(form, errors, {
        'bankTransit': null,
        'bankAccountNumber': null,
        'associatedAddress': null,
    });
    validateDashedNumberFields(form, errors, {
        'bankTransit': 9,
        'bankAccountNumber': { min: 8, max: 15 },
    });
    return errors;
}

export function validateDocumentFiles(documents: ExistingDocuments, hasPreviousPaymentProcessorInForm?: boolean) {
    const errors: { [T in DocumentType ]?: string } = {};
    if(!documents.proofOfBusiness)
        errors.proofOfBusiness = 'missing';
    if(!documents.voidCheck)
        errors.voidCheck = 'missing';
    if(hasPreviousPaymentProcessorInForm && !documents.previousPaymentProcessor)
        errors.previousPaymentProcessor = 'missing';
    return errors;
}


/** Helper functions */

function validateFields<O, T>(
    form: O,
    errorsObject: FormErrors<O>,
    fields: { [K in keyof O]: string | null },
    validation: (value: T) => string | null
) {
    const keys = Object.keys(fields) as any as (keyof O)[];
    for(const fieldName of keys) {
        const hasSameAsOption = fields[fieldName] !== null;
        if(hasSameAsOption) {
            const sameAsOtherField = form[fields[fieldName] as keyof typeof form] as boolean;
            if(sameAsOtherField)
                continue;
        }

        if(typeof errorsObject[fieldName] === 'string') {
            continue;
        }

        const error = validation(form[fieldName] as T);
        if(error)
            errorsObject[fieldName] = error;
    }
}

function validateRequiredTextFields<O>(
    form: O,
    errorsObject: FormErrors<O>,
    fields: { [K in keyof O]: string | null }
) {
    validateFields(form, errorsObject, fields, (value) => (!value || value === '') ? 'required' : null);
}

function validateRequiredNumberFields<O>(
    form: O,
    errorsObject: FormErrors<O>,
    fields: { [F in keyof O]: string | null }
) {
    validateFields(form, errorsObject, fields, (value) => (typeof value !== 'number') ? 'required' : null);
}

function validateDashedNumberFields<O>(
    form: O,
    errorsObject: FormErrors<O>,
    fields: { [F in keyof O]: { min: number, max: number} | number }
) {
    const keys = Object.keys(fields) as any as (keyof O)[];
    for(const fieldName of keys) {
        if(typeof errorsObject[fieldName] === 'string') {
            continue;
        }

        const digitsCount = (form[fieldName] as any).trim().replaceAll('-', '').length;
        const config = fields[fieldName];
        const min = typeof config !== 'number' ? config.min : config;
        const max = typeof config !== 'number' ? config.max : config;

        if(digitsCount < min || digitsCount > max)
            errorsObject[fieldName] = 'digitCount';

    }
}

function validatePhoneNumberFields<O>(
    form: O,
    errorsObject: FormErrors<O>,
    fields: { [F in keyof O]: string | null }
) {
    validateFields(form, errorsObject, fields, (value: string) => {
        if (value.length !== 0 && value.length !== 10) {
            return 'invalid';
        }
        return null;
    });
}

function validateEmailFields<O>(
    form: O,
    errorsObject: FormErrors<O>,
    fields: { [F in keyof O]: string | null }
) {
    validateFields(form, errorsObject, fields, (value: string) => {
        return isValidEmail(value) ? null : 'invalid';
    });
}

function validateDateFields<O>(
    form: O,
    errorsObject: FormErrors<O>,
    fields: { [F in keyof O]: string | null }
) {
    validateFields<O, Date | null>(form, errorsObject, fields, (value) => {
        if(!value)
            return 'required';
        if(isMoment(value))
            value = value.toDate();
        const future = Date.now() < value.getTime();
        return future ? 'future' : null;
    });
}

function validateAddress<O>(
    fieldName: keyof O,
    address: AddressComponents | null | undefined,
    errorsObject: FormErrors<O>,
) {
    if (!address)
        errorsObject[fieldName] = 'required';
    else if (typeof address === 'string')
        errorsObject[fieldName] = 'addressIsString';
    else if((address as AddressComponents).country !== 'US')
        errorsObject[fieldName] = 'usOnly';
    else if (addressComponentsEmpty(address))
        errorsObject[fieldName] = 'missingComponents';
}